import type { FC, ReactNode } from 'react'

import { memo, useEffect, useRef } from 'react'
import Router from 'next/router'

type Gsap = typeof import('gsap').gsap

const COLUMNS = { desktop: 16, tablet: 12, mobileLandscape: 10, mobile: 8 }
const PIXEL_DURATION = 0.06
const STAGGER_AMOUNT = 0.336

let gsapPromise: Promise<Gsap> | null = null

const loadGsap = (): Promise<Gsap> => {
    gsapPromise ??= import('gsap').then((mod) => mod.gsap)

    return gsapPromise
}

// Rebuilds the pixel grid so it always covers the viewport with square cells.
const buildGrid = (panel: HTMLElement): void => {
    const width = window.innerWidth
    const landscape = window.innerWidth > window.innerHeight

    let columns = COLUMNS.desktop

    if (width <= 479) columns = COLUMNS.mobile
    else if (width <= 767) columns = landscape ? COLUMNS.mobileLandscape : COLUMNS.mobile
    else if (width <= 991) columns = COLUMNS.tablet

    const rect = panel.getBoundingClientRect()
    const cellSize = rect.width / columns
    const rowCount = Math.ceil(rect.height / cellSize)

    panel.style.flexDirection = 'row'

    let cols = panel.querySelectorAll<HTMLElement>('[data-transition-col]')

    const template = cols[0]
    const pixel = template?.querySelector('[data-transition-pixel]')

    if (!template || !pixel) return

    if (cols.length !== columns) {
        const fragment = document.createDocumentFragment()

        for (let i = 0; i < columns; i++) fragment.appendChild(template.cloneNode(false))

        panel.replaceChildren(fragment)
        cols = panel.querySelectorAll<HTMLElement>('[data-transition-col]')
    }

    cols.forEach((col) => {
        col.style.flexDirection = 'column'
        col.style.flex = '1 1 auto'
        col.style.justifyContent = 'center'

        const missing = rowCount - col.childElementCount

        if (missing > 0) {
            const fragment = document.createDocumentFragment()

            for (let i = 0; i < missing; i++) fragment.appendChild(pixel.cloneNode(true))

            col.appendChild(fragment)
        }
        else if (missing < 0) {
            for (let i = missing; i < 0; i++) col.lastElementChild?.remove()
        }
    })
}

const PageTransitionPanel: FC = (): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const panel = panelRef.current

        if (!panel) return

        const motion = window.matchMedia('(prefers-reduced-motion: reduce)')

        let busy = false
        let covered = false
        let ownNavigation = false

        const clear = (): void => {
            panel.style.opacity = '0'
            covered = false
            busy = false
        }

        const cover = async (): Promise<void> => {
            const gsap = await loadGsap().catch(() => null)

            if (!gsap) return

            buildGrid(panel)

            const pixels = panel.querySelectorAll('[data-transition-pixel]')
            const timeline = gsap.timeline()

            covered = true
            timeline.set(panel, { opacity: 1, pointerEvents: 'none' }, 0)
            timeline.set(pixels, { opacity: 0, willChange: 'opacity' }, 0)
            timeline.to(pixels, { opacity: 1, duration: PIXEL_DURATION, ease: 'none', stagger: { amount: STAGGER_AMOUNT, from: 'random' } }, 0)

            await timeline.then()
        }

        const coverInstantly = (): void => {
            buildGrid(panel)

            panel.querySelectorAll<HTMLElement>('[data-transition-pixel]').forEach((pixel) => {
                pixel.style.opacity = '1'
            })

            panel.style.pointerEvents = 'none'
            panel.style.opacity = '1'
            covered = true
        }

        const reveal = async (): Promise<void> => {
            const gsap = await loadGsap().catch(() => null)

            if (!gsap) {
                clear()
                return
            }

            const pixels = panel.querySelectorAll('[data-transition-pixel]')
            const timeline = gsap.timeline()

            timeline.to(pixels, { opacity: 0, duration: PIXEL_DURATION, ease: 'none', stagger: { amount: STAGGER_AMOUNT, from: 'random' }, overwrite: 'auto' }, 0)
            timeline.set(panel, { opacity: 0 })
            timeline.set(pixels, { clearProps: 'willChange' })

            await timeline.then()

            covered = false
            busy = false
        }

        const navigate = async (href: string): Promise<void> => {
            busy = true
            ownNavigation = true

            await Promise.all([cover(), Router.prefetch(href.split('#')[0] || '/').catch(() => undefined)])

            Router.push(href).catch(() => {
                ownNavigation = false
                clear()
            })
        }

        const prefetched = new Set<string>()

        // Internal links are plain anchors, as on the original site: this resolves the ones the router should take over.
        const internalUrl = (target: EventTarget | null): URL | null => {
            const anchor = (target as Element | null)?.closest?.('a')

            if (!anchor || anchor.hasAttribute('download')) return null
            if (anchor.target && anchor.target !== '_self') return null

            const raw = anchor.getAttribute('href')

            if (!raw || raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:')) return null

            const url = new URL(anchor.href, window.location.href)

            if (url.origin !== window.location.origin || url.pathname.startsWith('/api/')) return null

            return url
        }

        const onClick = (event: MouseEvent): void => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

            const url = internalUrl(event.target)

            if (!url) return
            if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return

            event.preventDefault()

            if (motion.matches) {
                Router.push(`${url.pathname}${url.search}${url.hash}`)
                return
            }

            if (busy) return

            navigate(`${url.pathname}${url.search}${url.hash}`)
        }

        const onHover = (event: Event): void => {
            const url = internalUrl(event.target)

            if (!url || prefetched.has(url.pathname)) return

            prefetched.add(url.pathname)
            Router.prefetch(url.pathname).catch(() => undefined)
        }

        // Back and forward, or any navigation started elsewhere: cover at once, then dissolve the same way.
        const onStart = (): void => {
            if (ownNavigation || motion.matches || covered) return

            busy = true
            coverInstantly()
        }

        const onComplete = (): void => {
            ownNavigation = false

            if (motion.matches) {
                clear()
                return
            }

            if (!covered) {
                busy = false
                return
            }

            reveal().catch(clear)
        }

        document.addEventListener('click', onClick)
        document.addEventListener('mouseover', onHover, { passive: true })
        document.addEventListener('focusin', onHover)
        Router.events.on('routeChangeStart', onStart)
        Router.events.on('routeChangeComplete', onComplete)
        Router.events.on('routeChangeError', onComplete)

        return () => {
            document.removeEventListener('click', onClick)
            document.removeEventListener('mouseover', onHover)
            document.removeEventListener('focusin', onHover)
            Router.events.off('routeChangeStart', onStart)
            Router.events.off('routeChangeComplete', onComplete)
            Router.events.off('routeChangeError', onComplete)
        }
    }, [])

    return (
        <div data-transition-wrap='' className='transition' aria-hidden='true'>
            <div ref={panelRef} data-transition-panel='' className='transition__panel'>
                <div data-transition-col='' className='transition__col'>
                    <div data-transition-pixel='' className='transition__pixel' />
                </div>
            </div>
        </div>
    )
}

export const PageTransition = memo(PageTransitionPanel)
